import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from "react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { createSocket } from "../utils/socket";
import { useAuth } from "./AuthContext";

const NotificationsContext = createContext(null);

function getAnyToken() {
  return (
    localStorage.getItem("admin_token") ||
    localStorage.getItem("token") ||
    localStorage.getItem("auth_token") ||
    localStorage.getItem("user_token") ||
    null
  );
}

// ✅ normalize incoming type to match DB stable keys
function normalizeType(t) {
  const raw = String(t || "").trim();
  if (!raw) return "";

  const map = {
    // UI labels -> stable DB keys
    "System Update": "system_updates",
    "System Updates": "system_updates",
    "Health Alerts": "health_records",
    "Development Milestones": "development_milestones",
    "Donation Alerts": "donations",
    "Donation Alert": "donations",
    "User Activity": "user_activity",
    "Data Backup": "data_backup",
    Backup: "data_backup",

    // common backend variants
    system_update: "system_updates",
    system_updates: "system_updates",
    development_milestone: "development_milestones",
    development_milestones: "development_milestones",
    donation_alerts: "donations",
    donation: "donations",
    donations: "donations",
    useractivity: "user_activity",
    user_activity: "user_activity",
    data_backup: "data_backup",
    databackup: "data_backup",
    backup: "data_backup",
    health_records: "health_records",
    health_record: "health_records",

    // ✅ alerts
    alert: "alerts",
    alerts: "alerts",
  };

  if (map[raw]) return map[raw];

  return raw
    .toLowerCase()
    .replace(/[-\s]+/g, "_")
    .replace(/[^a-z0-9_]/g, "");
}

// ✅ build a stable key even if notif.id is missing
function buildKey(n) {
  if (!n) return "";
  if (n.dedupeKey) return String(n.dedupeKey);
  if (n.id) return `id:${n.id}`;

  const source = n.source || "system";
  const type = n.type || "";
  const entityId = n.entityId || n.refId || n.alertId || "";
  const action = n.action || "";
  const title = (n.title || "").trim();
  const message = (n.message || "").trim();
  const createdAt = n.createdAt || n.timestamp || "";

  return `${source}:${type}:${entityId}:${action}:${title}:${message}:${createdAt}`;
}

export function NotificationsProvider({ children }) {
  const { authFetch, user } = useAuth();
  const navigate = useNavigate();

  const socketRef = useRef(null);

  const enabledTypesRef = useRef(new Set());
  const [enabledTypes, setEnabledTypes] = useState(new Set());

  const [items, setItems] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("rt_notifications") || "[]");
    } catch {
      return [];
    }
  });

  // ✅ fast dedupe memory
  const seenKeysRef = useRef(new Set());
  useEffect(() => {
    const s = new Set();
    for (const n of items) s.add(buildKey(n));
    seenKeysRef.current = s;
  }, [items]);

  // ✅ toast dedupe memory
  const toastedKeysRef = useRef(new Set());

  const unread = useMemo(() => items.filter((n) => !n.read).length, [items]);

  const refreshEnabledTypes = useCallback(async () => {
    try {
      if (!user) return enabledTypesRef.current;

      const res = await authFetch("/settings", { method: "GET" });
      if (!res) return enabledTypesRef.current;
      if (res.status === 401) return enabledTypesRef.current;

      const data = await res.json().catch(() => ({}));

      const set = new Set(
        (data?.notifState || [])
          .filter((x) => x.enabled)
          .map((x) => normalizeType(x.type))
          .filter(Boolean)
      );

      enabledTypesRef.current = set;
      setEnabledTypes(set);
      return set;
    } catch {
      return enabledTypesRef.current;
    }
  }, [authFetch, user]);

  // ✅ load persisted alerts for offline users from backend inbox
  const loadPersistedAlertInbox = useCallback(async () => {
    try {
      if (!user) return;

      const res = await authFetch("/alerts/inbox", { method: "GET" });
      if (!res || res.status === 401 || res.status === 403) return;

      const data = await res.json().catch(() => ({}));
      const incoming = Array.isArray(data?.items) ? data.items : [];
      if (!incoming.length) return;

      setItems((prev) => {
        const map = new Map();

        for (const n of prev) {
          map.set(buildKey(n), n);
        }

        for (const n of incoming) {
          const fixed = {
            ...n,
            source: "alerts",
            type: normalizeType(n.type || "alerts"),
            read: !!n.read,
            clickPath:
              n.clickPath ||
              (n.alertId || n.entityId
                ? `/children/alerts?open=${n.alertId || n.entityId}`
                : "/children/alerts"),
          };
          map.set(buildKey(fixed), fixed);
        }

        const next = Array.from(map.values())
          .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0))
          .slice(0, 50);

        localStorage.setItem("rt_notifications", JSON.stringify(next));
        seenKeysRef.current = new Set(next.map(buildKey));
        return next;
      });
    } catch (e) {
      console.error("Failed to load persisted alert inbox:", e);
    }
  }, [authFetch, user]);

  useEffect(() => {
    let mounted = true;

    (async () => {
      if (!user) return;
      const set = await refreshEnabledTypes();
      if (!mounted) return;
      enabledTypesRef.current = set;
    })();

    return () => {
      mounted = false;
    };
  }, [refreshEnabledTypes, user]);

  // ✅ load persisted alert inbox when user logs in
  useEffect(() => {
    if (!user) return;
    loadPersistedAlertInbox();
  }, [user, loadPersistedAlertInbox]);

  // logout cleanup
  useEffect(() => {
    if (user) return;

    if (socketRef.current) {
      try {
        socketRef.current.disconnect();
      } catch {}
      socketRef.current = null;
    }

    enabledTypesRef.current = new Set();
    setEnabledTypes(new Set());

    seenKeysRef.current = new Set();
    toastedKeysRef.current = new Set();
  }, [user]);

  function defaultClickPath(n) {
    const type = normalizeType(n?.type);
    const source = String(n?.source || "").toLowerCase();

    const childId = n?.childId ?? n?.entityId ?? null;

    // Alerts
    if (source === "alerts" || type === "alerts") {
      const id = n?.alertId ?? n?.entityId ?? null;
      if (id) return `/children/alerts?open=${id}`;
      return "/children/alerts";
    }

    // Settings
    if (type === "system_updates" || type === "data_backup") return "/setting";

    // Health
    if (type === "health_records") {
      if (childId) return `/children?open=${childId}`;
      return "/children";
    }

    // Development
    if (type === "development_milestones") {
      if (childId) return `/children?dev=${childId}`;
      return "/children/development";
    }

    // Donations
    if (type === "donations") {
      const id = n?.donationId ?? n?.entityId ?? null;
      if (id) return `/children/donations?view=donations&open=${id}`;
      return "/children/donations?view=donations";
    }

    // Users
    if (type === "user_activity") {
      const id = n?.userId ?? n?.entityId ?? null;
      if (id) return `/users?openUser=${id}`;
      return "/users";
    }

    return "/notifications";
  }

  // ✅ mark single notification read
  const markRead = useCallback(
    async (notif) => {
      const key = buildKey(notif);
      if (!key) return;

      setItems((prev) => {
        const next = prev.map((n) =>
          buildKey(n) === key ? { ...n, read: true } : n
        );
        localStorage.setItem("rt_notifications", JSON.stringify(next));
        return next;
      });

      try {
        if (notif?.source === "alerts" && notif?.recipientId) {
          await authFetch(`/alerts/inbox/${notif.recipientId}/read`, {
            method: "PATCH",
          });
        }
      } catch (e) {
        console.error("Failed to mark alert notif as read:", e);
      }
    },
    [authFetch]
  );

  // ✅ open notification + mark read
  const openNotification = useCallback(
    async (notif, navFn) => {
      if (!notif) return;

      const key = buildKey(notif);
      if (key) {
        setItems((prev) => {
          const next = prev.map((n) =>
            buildKey(n) === key ? { ...n, read: true } : n
          );
          localStorage.setItem("rt_notifications", JSON.stringify(next));
          return next;
        });
      }

      try {
        if (notif?.source === "alerts" && notif?.recipientId) {
          await authFetch(`/alerts/inbox/${notif.recipientId}/read`, {
            method: "PATCH",
          });
        }
      } catch (e) {
        console.error("Failed to mark alert notif as read:", e);
      }

      if (notif.clickPath && typeof navFn === "function") {
        navFn(notif.clickPath);
        return;
      }
      if (notif.clickUrl) {
        window.open(notif.clickUrl, "_blank", "noopener,noreferrer");
        return;
      }

      const path = defaultClickPath(notif);
      if (typeof navFn === "function") navFn(path);
    },
    [authFetch]
  );

  const showToastForNotif = useCallback(
    (n) => {
      if (!n) return;

      const key = buildKey(n);
      if (!key) return;

      if (toastedKeysRef.current.has(key)) return;
      toastedKeysRef.current.add(key);

      const title = (n.title || "New notification").trim();
      const message = (n.message || "").trim();

      toast.custom(
        (t) => (
          <div className="pointer-events-auto w-full max-w-sm overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-xl dark:border-gray-800 dark:bg-gray-900">
            <div className="h-1 w-full bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600" />

            <div className="p-4">
              <div className="flex items-start gap-3">
                <div className="mt-0.5 flex h-10 w-10 items-center justify-center rounded-xl bg-blue-50 text-blue-700 dark:bg-blue-950/30 dark:text-blue-200">
                  <span className="text-lg">🔔</span>
                </div>

                <div className="min-w-0 flex-1">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="truncate text-sm font-semibold text-slate-900 dark:text-gray-100">
                        {title}
                      </p>
                      {message ? (
                        <p className="mt-1 line-clamp-2 text-sm text-slate-600 dark:text-gray-300">
                          {message}
                        </p>
                      ) : null}
                    </div>

                    <button
                      type="button"
                      className="rounded-lg p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-gray-800 dark:hover:text-gray-200"
                      onClick={() => toast.dismiss(t.id)}
                      aria-label="Close"
                    >
                      ✕
                    </button>
                  </div>

                  <div className="mt-3 flex items-center justify-between">
                    <span className="text-xs text-slate-400 dark:text-gray-400">
                      {new Date().toLocaleTimeString([], {
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </span>

                    <div className="flex gap-2">
                      <button
                        type="button"
                        className="rounded-xl bg-slate-900 px-3 py-1.5 text-sm font-medium text-white hover:opacity-90 dark:bg-gray-100 dark:text-gray-900"
                        onClick={() => {
                          openNotification(n, navigate);
                          toast.dismiss(t.id);
                        }}
                      >
                        View
                      </button>
                      <button
                        type="button"
                        className="rounded-xl bg-slate-100 px-3 py-1.5 text-sm font-medium text-slate-800 hover:bg-slate-200 dark:bg-gray-800 dark:text-gray-100 dark:hover:bg-gray-700"
                        onClick={() => toast.dismiss(t.id)}
                      >
                        Dismiss
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ),
        { id: `notif:${key}`, duration: 5000 }
      );
    },
    [navigate, openNotification]
  );

  // socket connect + listener
  useEffect(() => {
    const token = getAnyToken();
    const uid = user?.id;

    if (!token || !uid) return;

    if (socketRef.current) {
      try {
        socketRef.current.disconnect();
      } catch {}
      socketRef.current = null;
    }

    const s = createSocket(token);
    if (!s) return;

    socketRef.current = s;

    const onConnectError = (err) => {
      console.error("Socket error:", err?.message || err);
    };

    const onNotif = (notif) => {
      if (!notif) return;

      const fixed = {
        ...notif,
        type: normalizeType(notif.type),
      };

      const isAlerts =
        fixed?.source === "alerts" || normalizeType(fixed?.type) === "alerts";

      // ✅ settings filter applies only to non-alerts
      const enabledSet = enabledTypesRef.current;
      if (!isAlerts && enabledSet.size && fixed?.type && !enabledSet.has(fixed.type)) {
        return;
      }

      const key = buildKey(fixed);
      if (!key) return;

      if (seenKeysRef.current.has(key)) return;

      setItems((prev) => {
        const exists =
          prev.some((x) => buildKey(x) === key) ||
          (fixed?.id && prev.some((x) => x.id === fixed.id));

        if (exists) {
          seenKeysRef.current.add(key);
          return prev;
        }

        const created = { ...fixed, read: false };
        const next = [created, ...prev].slice(0, 50);

        localStorage.setItem("rt_notifications", JSON.stringify(next));
        seenKeysRef.current.add(key);

        showToastForNotif(created);

        return next;
      });
    };

    s.on("connect_error", onConnectError);
    s.on("notification:new", onNotif);

    return () => {
      try {
        s.off("connect_error", onConnectError);
        s.off("notification:new", onNotif);
        s.disconnect();
      } catch {}
      socketRef.current = null;
    };
  }, [user?.id, showToastForNotif]);

  const markAllRead = useCallback(async () => {
    const alertRecipientIds = items
      .filter((n) => !n.read && n?.source === "alerts" && n?.recipientId)
      .map((n) => n.recipientId);

    setItems((prev) => {
      const next = prev.map((n) => ({ ...n, read: true }));
      localStorage.setItem("rt_notifications", JSON.stringify(next));
      return next;
    });

    try {
      await Promise.all(
        alertRecipientIds.map((id) =>
          authFetch(`/alerts/inbox/${id}/read`, { method: "PATCH" })
        )
      );
    } catch (e) {
      console.error("Failed to mark all alert notifications as read:", e);
    }
  }, [authFetch, items]);

  const clearAll = useCallback(() => {
    setItems([]);
    localStorage.setItem("rt_notifications", "[]");
    seenKeysRef.current = new Set();
    toastedKeysRef.current = new Set();
  }, []);

  const value = useMemo(
    () => ({
      items,
      unread,
      enabledTypes,
      refreshEnabledTypes,
      markAllRead,
      clearAll,
      markRead,
      openNotification,
    }),
    [
      items,
      unread,
      enabledTypes,
      refreshEnabledTypes,
      markAllRead,
      clearAll,
      markRead,
      openNotification,
    ]
  );

  return (
    <NotificationsContext.Provider value={value}>
      {children}
    </NotificationsContext.Provider>
  );
}

export function useNotifications() {
  const ctx = useContext(NotificationsContext);
  if (!ctx)
    throw new Error("useNotifications must be used within NotificationsProvider");
  return ctx;
}